<script lang="ts" setup>
import { computed } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import RecordingSpinner from '@/components/RecordingSpinner.vue'
import { useUtil } from '@/composables/util'
import { PHRASE_LEVEL_SENTENCE, PHRASE_LEVEL_WORDS } from '@/composables/versePhraser'
import { MANUAL_PHRASE_SPLIT_KEY, TOOLTIP_OPEN_DELAY_MS } from '@/constants/practiceConstants'
import { type SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'

// {{{ props
interface Props {
  isRecording: boolean
  isReadyForRecording: boolean
  uploadWaiting: boolean
  pendingUploadRecording: File | null
  backgroundSampling: boolean
  disableAutomaticVoiceStartStop: boolean
  displayGraph: boolean
  phraseLevel: number
  segmentationLength: number
  existingSentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios | null
  playing: boolean
}

const props = defineProps<Props>()
// }}}

// {{{ emits
interface Emits {
  (e: 'startRecord', mode: 'single' | 'continuous'): void
  (e: 'toggleRecord'): void
  (e: 'uploadFile'): void
  (e: 'update:pendingUploadRecording', value: File | null): void
  (e: 'update:disableAutomaticVoiceStartStop', value: boolean): void
  (e: 'update:displayGraph', value: boolean): void
  (e: 'warnManualSegmentation'): void
}

const emit = defineEmits<Emits>()
// }}}

// {{{ composables
const logger = useLogger()
const { isDevelopment } = useUtil(logger)
// }}}

// {{{ computed
const localPendingUploadRecording = computed({
  get: () => props.pendingUploadRecording,
  set: (value) => emit('update:pendingUploadRecording', value),
})

const localDisableAutomaticVoiceStartStop = computed({
  get: () => props.disableAutomaticVoiceStartStop,
  set: (value) => emit('update:disableAutomaticVoiceStartStop', value),
})

const localDisplayGraph = computed({
  get: () => props.displayGraph,
  set: (value) => emit('update:displayGraph', value),
})

const isRecordingDisabled = computed(() => {
  return (
    props.playing || // can't record while playing audio
    !props.isReadyForRecording || // selections aren't made
    props.uploadWaiting || // we're in the middle of an upload
    !!props.pendingUploadRecording || // in the middle of an upload
    (props.phraseLevel > PHRASE_LEVEL_SENTENCE && props.phraseLevel < PHRASE_LEVEL_WORDS && props.segmentationLength === 1) // phrase mode but didn't create a phrasing
  )
})

// }}}

// {{{ methods
const handleToggleManualMode = () => {
  localDisableAutomaticVoiceStartStop.value = !localDisableAutomaticVoiceStartStop.value
  emit('warnManualSegmentation')
}

const handleToggleRecord = () => {
  console.log(
    'RecordingControls: toggleRecord, isRecording =',
    props.isRecording,
    'backgroundSampling =',
    props.backgroundSampling,
    'isReadyForRecording =',
    props.isReadyForRecording,
  )
  emit('toggleRecord')
}
// }}}
</script>

<template>
  <!-- Recording Controls -->
  <v-form @submit.prevent="emit('uploadFile')">
    <div class="recording-controls-row">
      <!-- Stop button (during recording) -->
      <template v-if="isRecording && !backgroundSampling">
        <v-btn color="warning" :disabled="isRecordingDisabled" @click="handleToggleRecord">
          <v-icon class="mr-2" size="small">mdi-stop</v-icon>
          Stop
        </v-btn>
        <recording-spinner />
      </template>

      <!-- Record + Record Continuous buttons (when not recording) -->
      <template v-else>
        <v-btn color="primary" :disabled="isRecordingDisabled" @click="emit('startRecord', 'single')">
          <span class="mr-2"><font-awesome-icon icon="microphone" /></span>
          Record
        </v-btn>
        <v-btn
          color="primary"
          :disabled="isRecordingDisabled || !!existingSentenceRecordingMetadataWithPitchedAudios"
          @click="emit('startRecord', 'continuous')"
        >
          <span class="mr-2"><font-awesome-icon icon="microphone" /></span>
          Record continuous
        </v-btn>
      </template>

      <v-progress-circular v-if="!isRecording && uploadWaiting" indeterminate size="32" />

      <!-- Toggle options (only when not recording/uploading) -->
      <template v-if="!isRecording && !uploadWaiting">
        <v-tooltip
          bottom
          :open-delay="TOOLTIP_OPEN_DELAY_MS"
          :text="`Disables automatic voice phrase recognition. You need to press '${MANUAL_PHRASE_SPLIT_KEY}' at the end of each phrase that you record.`"
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              :active="disableAutomaticVoiceStartStop"
              color="primary"
              size="small"
              :variant="disableAutomaticVoiceStartStop ? 'flat' : 'outlined'"
              @click="handleToggleManualMode"
            >
              <v-icon left size="small">mdi-microphone-off</v-icon>
              Manual mode
            </v-btn>
          </template>
        </v-tooltip>
        <v-btn
          v-if="isDevelopment()"
          :active="displayGraph"
          color="primary"
          :disabled="disableAutomaticVoiceStartStop"
          size="small"
          :variant="displayGraph ? 'flat' : 'outlined'"
          @click="localDisplayGraph = !localDisplayGraph"
        >
          <v-icon left size="small">mdi-chart-line</v-icon>
          Graph
        </v-btn>
      </template>
    </div>

    <!-- File Upload -->
    <div v-if="!isRecording" class="recording-upload-row">
      <v-file-input
        v-model="localPendingUploadRecording"
        accept="audio/*"
        density="compact"
        :disabled="isRecording || !isReadyForRecording"
        drop-placeholder="Drop file here"
        hide-details
        label="...or upload audio file"
        style="max-width: 300px"
        variant="underlined"
      />
      <v-btn v-if="pendingUploadRecording" color="primary" :disabled="!isReadyForRecording || uploadWaiting" size="small" type="submit">
        Upload
      </v-btn>
    </div>
  </v-form>
</template>

<style scoped>
.recording-controls-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 4px 0;
}

.recording-upload-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
}
</style>
